import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import { useAuth } from "../../context/AuthContext";
import { fetchEnrollmentDetail, fetchEnrollmentAssessments, fetchLessonAssessmentClient } from "./clientCourseService";
import "./courses.css";

const CRITERIA_LABELS = {
  scoreFluency:     "Fluency",
  scoreClarity:     "Clarity",
  scoreVocabulary:  "Vocabulary",
  scoreGrammar:     "Grammar",
  scoreInteraction: "Interaction",
};

const MATERIAL_ICON = { GRAMMAR: "📖", READING: "📄" };
const MATERIAL_NAV  = {
  GRAMMAR: (id) => `/study/grammar/sub-topic/${id}`,
  READING: (id) => `/study/reading/${id}`,
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
};

const fmtDateOnly = (str) => {
  if (!str) return "TBD";
  return new Date(str + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/* ── Lesson assessment panel (inline in schedule) ── */
const LessonAssessPanel = ({ enrollmentId, lesson }) => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!lesson.curriculumId) { setLoading(false); return; }
    fetchLessonAssessmentClient(enrollmentId, lesson.curriculumId)
      .then((res) => {
        const arr = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
        setData({
          initial: arr.find((a) => a.assessmentType === "INITIAL") || null,
          final:   arr.find((a) => a.assessmentType === "FINAL")   || null,
        });
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [enrollmentId, lesson.curriculumId]);

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  if (loading) return <div style={{ padding: "0.75rem 0", color: "#9ca3af", fontSize: "0.82rem" }}>Loading assessment…</div>;
  if (!data?.initial && !data?.final) return (
    <div style={{ padding: "0.75rem 0", color: "#9ca3af", fontSize: "0.82rem" }}>No assessment recorded for this lesson yet.</div>
  );

  const ScoreRow = ({ label, score }) => {
    const color = score >= 5 ? "#16a34a" : score >= 4 ? "#65a30d" : score >= 3 ? "#d97706" : score >= 2 ? "#ea580c" : "#dc2626";
    return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
      <span style={{ fontSize: "0.78rem", color: "#64748b", width: 80, flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1, height: 5, background: "#e5e7eb", borderRadius: 3 }}>
        <div style={{ width: `${(score / 5) * 100}%`, height: "100%", background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: "0.78rem", fontWeight: 700, color, width: 28, textAlign: "right" }}>{score}/5</span>
    </div>
    );
  };

  const MiniCard = ({ label, a, badgeColor }) => (
    <div style={{ flex: 1, minWidth: 200, background: "#f8fafc", borderRadius: 8, padding: "0.75rem 1rem", border: "1px solid #e2e8f0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
        <span style={{ fontSize: "0.72rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: badgeColor }}>{label}</span>
        <span style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>{a.totalScore}<span style={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 400 }}>/25</span></span>
      </div>
      {Object.entries(CRITERIA_LABELS).map(([k, lbl]) => <ScoreRow key={k} label={lbl} score={a[k] || 0} />)}
      {a.improvementGoals && (
        <div style={{ margin: "10px 0 0", background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "6px 10px" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>🎯 Goals</div>
          <div style={{ fontSize: "0.82rem", color: "#78350f", lineHeight: 1.4 }}>{a.improvementGoals}</div>
        </div>
      )}
      {a.instructorFeedback && (
        <div style={{ margin: "8px 0 0", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 6, padding: "6px 10px" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, color: "#1e40af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>💬 Feedback</div>
          <div style={{ fontSize: "0.82rem", color: "#1e3a8a", fontStyle: "italic", lineHeight: 1.4 }}>"{a.instructorFeedback}"</div>
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: "0.72rem", color: "#9ca3af" }}>
        {a.assessedByName ? `By ${a.assessedByName} · ${fmtDate(a.assessedAt)}` : fmtDate(a.assessedAt)}
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: "0.75rem", paddingTop: "0.75rem", borderTop: "1px dashed #e2e8f0" }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: "#7c3aed", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.6rem" }}>
        Lesson Assessment
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {data.initial && <MiniCard label="Initial" a={data.initial} badgeColor="#2563eb" />}
        {data.final   && <MiniCard label="Final"   a={data.final}   badgeColor="#16a34a" />}
      </div>
    </div>
  );
};

/* ── Schedule tab ── */
const ScheduleTab = ({ meetings, enrollmentId, navigate, backUrl }) => {
  const [expandedIdx, setExpandedIdx] = useState(null);

  if (!meetings?.length) {
    return <div className="cc-empty"><p>No sessions scheduled yet.</p></div>;
  }
  return (
    <div className="cc-schedule-wrap">
      {meetings.map((m, idx) => {
        const statusCls =
          m.attendanceStatus === "ATTENDED"   ? "attended" :
          m.attendanceStatus === "ABSENT"     ? "absent"   :
          m.attendanceStatus === "SCHEDULED"  ? "scheduled" : "unscheduled";
        const statusLabel =
          m.attendanceStatus === "ATTENDED"  ? "✓ Attended" :
          m.attendanceStatus === "ABSENT"    ? "✗ Absent"   :
          m.attendanceStatus === "SCHEDULED" ? "Scheduled"  : null;
        const isExpanded = expandedIdx === idx;
        const lessonCurriculumId = m.curriculumId;

        return (
          <div key={idx} className={`cc-session-row cc-session-${statusCls}`}
            style={{ flexDirection: "column", alignItems: "stretch" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div className="cc-session-day">
                <span className="cc-session-day-label">Day</span>
                <span className="cc-session-day-num">{m.dayNumber ?? idx + 1}</span>
              </div>
              <div className="cc-session-info">
                <div className="cc-session-title">
                  {m.title || "—"}
                  {m.isAssessment && <span className="cc-session-assess-badge">★ Assessment</span>}
                </div>
                <div className="cc-session-meta">
                  {m.scheduledAt && (
                    <span className="cc-session-time">🕐 {fmtDateTime(m.scheduledAt)}</span>
                  )}
                  {statusLabel && (
                    <span className={`cc-session-status-badge cc-session-status-${statusCls}`}>{statusLabel}</span>
                  )}
                </div>
              </div>
              <div className="cc-session-right">
                {m.isJoinable && m.zoomJoinUrl ? (
                  <a href={m.zoomJoinUrl} target="_blank" rel="noreferrer"
                    className="cc-btn cc-btn-primary cc-session-join-btn"
                    onClick={(e) => e.stopPropagation()}>
                    🎥 Join Now
                  </a>
                ) : m.zoomJoinUrl && !m.isJoinable ? (
                  <span className="cc-session-soon">⏳ Opens soon</span>
                ) : !m.scheduledAt ? (
                  <span className="cc-session-unscheduled-label">Not scheduled</span>
                ) : null}
                {m.studyMaterials?.length > 0 && (
                  <button
                    className="cc-session-action-btn materials"
                    onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                  >
                    📚 {m.studyMaterials.length} Material{m.studyMaterials.length > 1 ? "s" : ""}
                  </button>
                )}
                <button
                  className="cc-session-action-btn feedback"
                  onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                >
                  {isExpanded ? "Feedback ▲" : "Feedback ▼"}
                </button>
              </div>
            </div>
            {isExpanded && (
              <div className="cc-session-expand">
                {m.studyMaterials?.length > 0 && (
                  <div style={{ marginBottom: "1rem" }}>
                    <div className="cc-session-expand-section-label" style={{ color: "#6d28d9" }}>📚 Study Materials</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                      {m.studyMaterials.map((mat) => (
                        <button
                          key={mat.id}
                          className="cc-session-material-card"
                          onClick={() => navigate(MATERIAL_NAV[mat.contentType]?.(mat.contentId) || "#", { state: { backUrl } })}
                          style={{
                            background: mat.contentType === "GRAMMAR" ? "#ede9fe" : "#dbeafe",
                            color: mat.contentType === "GRAMMAR" ? "#6d28d9" : "#1d4ed8",
                            borderColor: mat.contentType === "GRAMMAR" ? "#c4b5fd" : "#bfdbfe",
                          }}
                        >
                          <span>{MATERIAL_ICON[mat.contentType]} {mat.contentTitle}</span>
                          {mat.label && (
                            <span style={{ fontSize: "0.7rem", fontWeight: 400, opacity: 0.75, marginTop: 2 }}>{mat.label}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <LessonAssessPanel enrollmentId={enrollmentId} lesson={m} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ── Attendance tab ── */
const AttendanceTab = ({ attendance }) => {
  if (!attendance?.length) {
    return <div className="cc-empty"><p>No attendance records yet.</p></div>;
  }

  const attended = attendance.filter((a) => a.attendanceStatus === "ATTENDED").length;
  const total    = attendance.filter((a) => a.attendanceStatus != null).length;

  return (
    <div>
      <div style={{
        background: "#f8fafc", borderRadius: 10, padding: "12px 16px",
        marginBottom: 20, display: "flex", gap: 24, flexWrap: "wrap",
        fontSize: "0.88rem", color: "#374151",
      }}>
        <span>✅ Attended: <strong>{attended}</strong></span>
        <span>❌ Absent: <strong>{total - attended}</strong></span>
        <span>📊 Rate: <strong>{total > 0 ? Math.round((attended / total) * 100) : 0}%</strong></span>
      </div>

      <div className="cc-attend-grid">
        {attendance.map((a) => {
          const cls =
            a.attendanceStatus === "ATTENDED" ? "attended" :
            a.attendanceStatus === "ABSENT"   ? "absent"   : "scheduled";
          const icon =
            a.attendanceStatus === "ATTENDED" ? "✓" :
            a.attendanceStatus === "ABSENT"   ? "✗" : "○";
          return (
            <div key={a.dayNumber} className="cc-attend-cell">
              <div className={`cc-attend-dot ${cls}`}>{icon}</div>
              <span className="cc-attend-label">D{a.dayNumber}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Assessment tab ── */
const AssessmentTab = ({ enrollmentId }) => {
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    fetchEnrollmentAssessments(enrollmentId)
      .then((res) => { if (res.data) setSummary(res.data); })
      .finally(() => setLoading(false));
  }, [enrollmentId]);


  if (loading) return <div className="cc-loading"><div className="cc-spinner" /></div>;

  const fmtDate = (iso) => iso
    ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  const ScoreBar = ({ label, score }) => {
    const color = score >= 5 ? "#16a34a" : score >= 4 ? "#65a30d" : score >= 3 ? "#d97706" : score >= 2 ? "#ea580c" : "#dc2626";
    return (
    <div className="cc-assess-row">
      <span className="cc-assess-label">{label}</span>
      <div className="cc-assess-bar-wrap">
        <div className="cc-assess-bar-fill" style={{ width: `${(score / 5) * 100}%`, background: color }} />
      </div>
      <span className="cc-assess-score" style={{ color }}>{score}/5</span>
    </div>
    );
  };

  const AssessCard = ({ label, a }) => {
    if (!a) return (
      <div className="cc-assess-card" style={{ flex: 1 }}>
        <h3>{label}</h3>
        <div className="cc-empty" style={{ padding: "1rem 0" }}><p style={{ margin: 0 }}>Not yet completed.</p></div>
      </div>
    );
    return (
      <div className="cc-assess-card" style={{ flex: 1 }}>
        <h3>{label}</h3>
        {Object.entries(CRITERIA_LABELS).map(([k, lbl]) => (
          <ScoreBar key={k} label={lbl} score={a[k] || 0} />
        ))}
        <div className="cc-assess-total"><span>Total</span><span>{a.totalScore}/25</span></div>
        {a.instructorFeedback && (
          <p style={{ marginTop: 10, fontSize: "0.85rem", color: "#64748b", fontStyle: "italic" }}>"{a.instructorFeedback}"</p>
        )}
        {a.improvementGoals && (
          <p style={{ marginTop: 6, fontSize: "0.82rem", color: "#64748b" }}>Goals: {a.improvementGoals}</p>
        )}
        <div style={{ marginTop: 8, fontSize: "0.78rem", color: "#9ca3af" }}>
          {a.assessedByName ? `By ${a.assessedByName} · ${fmtDate(a.assessedAt)}` : fmtDate(a.assessedAt)}
        </div>
      </div>
    );
  };

  // course-level from summary API
  const cInitial  = summary?.initial;
  const cFinal    = summary?.finalAssessment;
  const delta     = summary?.scoreDelta;
  const improved  = summary?.improved;

  return (
    <div>
      {/* ── Course Progress ── */}
      <div>
          <div style={{ fontSize: "0.78rem", fontWeight: 700, color: "#028891", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.75rem" }}>
            Your Course Assessment
          </div>
          {delta != null && (
            <div className={`cc-assess-delta ${improved ? "positive" : "negative"}`} style={{ marginBottom: 16, display: "inline-block" }}>
              {improved ? `\ud83c\udf89 You improved by ${delta} points!` : `\u25bc ${Math.abs(delta)} points change`}
            </div>
          )}
          {!cInitial && <div style={{ color: "#9ca3af", fontSize: "0.88rem", marginBottom: 12 }}>Initial assessment not done yet.</div>}
          {cInitial && !cFinal && <div style={{ color: "#9ca3af", fontSize: "0.88rem", marginBottom: 12 }}>Final assessment pending.</div>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
            <AssessCard label="Initial Assessment" a={cInitial} />
            <AssessCard label="Final Assessment"   a={cFinal} />
          </div>
      </div>
    </div>
  );
};


/* ─────────────────────────────────────────────
   Main page
───────────────────────────────────────────── */
const MyEnrollmentDetailPage = () => {
  const { enrollmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authLoading } = useAuth();

  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [activeTab, setActiveTab]   = useState("schedule");

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login?redirect=/my-courses", { replace: true }); return; }
    fetchEnrollmentDetail(enrollmentId)
      .then((res) => setEnrollment(res.data))
      .catch(() => setEnrollment(null))
      .finally(() => setLoading(false));
  }, [enrollmentId, authLoading, user, navigate]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="cc-loading"><div className="cc-spinner" /><p>Loading…</p></div>
      </>
    );
  }

  if (!enrollment) {
    return (
      <>
        <Navbar />
        <div className="cc-empty">
          <div className="cc-empty-icon">❌</div>
          <p>Enrollment not found.</p>
          <button className="cc-btn cc-btn-outline" onClick={() => navigate("/my-courses")}>
            ← Back to My Courses
          </button>
        </div>
      </>
    );
  }

  // Guard: PENDING_PAYMENT enrollments have no lessons yet
  if (enrollment.status === "PENDING_PAYMENT") {
    return (
      <>
        <Navbar />
        <div className="cc-page" style={{ paddingTop: 24 }}>
          <button
            className="cc-btn cc-btn-outline"
            style={{ marginBottom: 16, padding: "6px 14px", fontSize: "0.82rem" }}
            onClick={() => navigate("/my-courses")}
          >
            ← My Courses
          </button>
          <div style={{ background: "#fffbeb", borderRadius: 12, padding: "2rem", maxWidth: 480, textAlign: "center" }}>
            <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>⏳</div>
            <h2 style={{ margin: "0 0 0.5rem", color: "#92400e" }}>Awaiting Confirmation</h2>
            <p style={{ color: "#4b5563", fontSize: "0.9rem", margin: 0 }}>
              Your enrollment for <strong>{enrollment.courseName}</strong> ({enrollment.batchName}) is pending payment confirmation.
              Admin will contact you on your registered WhatsApp/phone.
            </p>
          </div>
        </div>
      </>
    );
  }

  const lessons    = enrollment.lessons || [];
  const meetings   = lessons;
  const attendance = lessons.filter((l) => l.attendanceStatus != null);

  return (
    <>
      <Navbar />
      <div className="cc-page" style={{ paddingTop: 24 }}>

        {/* back */}
        <button
          className="cc-btn cc-btn-outline"
          style={{ marginBottom: 16, padding: "6px 14px", fontSize: "0.82rem" }}
          onClick={() => navigate("/my-courses")}
        >
          ← My Courses
        </button>

        {/* header card */}
        <div className="cc-enroll-header">
          <div className="cc-enroll-header-info">
            <div className="cc-enroll-header-chips">
              <span className={`cc-enroll-status cc-enroll-${enrollment.status}`}>
                {enrollment.status === "ACTIVE" ? "Active" : enrollment.enrollmentStatus || enrollment.status}
              </span>
              <span className={`cc-mode cc-mode-${enrollment.mode}`}>
                {enrollment.mode === "BATCH" ? "👥 Batch" : "👤 Personal Coaching"}
              </span>
            </div>
            <h2 className="cc-enroll-course-name">{enrollment.courseName}</h2>
            <div className="cc-enroll-header-meta">
              <span>👨🏫 {enrollment.instructorName || "TBD"}</span>
              <span>📅 Starts {fmtDateOnly(enrollment.batchStartDate)}</span>
              {enrollment.attendedCount != null && enrollment.totalLessons != null && (
                <span>✅ {enrollment.attendedCount}/{enrollment.totalLessons} sessions completed</span>
              )}
            </div>
          </div>
        </div>

        {/* tabs */}
        <div className="cc-tabs">
          {["schedule", "attendance", "assessment"].map((tab) => (
            <button
              key={tab}
              className={`cc-tab ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === "schedule" && meetings.length > 0 && (
                <span style={{
                  marginLeft: 5, background: "#e0f2fe", color: "#0369a1",
                  borderRadius: 999, padding: "0 6px", fontSize: "0.72rem",
                }}>
                  {meetings.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === "schedule"   && <ScheduleTab meetings={meetings} enrollmentId={enrollmentId} navigate={navigate} backUrl={location.pathname} />}
        {activeTab === "attendance" && <AttendanceTab attendance={attendance} />}
        {activeTab === "assessment" && <AssessmentTab enrollmentId={enrollmentId} />}
      </div>
    </>
  );
};

export default MyEnrollmentDetailPage;
